import { useCallback } from 'react';
import { useFieldArray } from 'react-hook-form';
import { nanoid } from 'nanoid';

import {
  MAX_MATCH_PAIR_COUNT,
  MIN_MATCH_PAIR_COUNT,
} from '../../../form/schema/quiz/QuizMatchDataSchema';
import { focusField } from '../../focus/focusUtils';
import { type BuildFormControl } from '../../type';

export const useMatchPairs = ({
  control,
  index,
}: {
  control: BuildFormControl;
  index: number;
}) => {
  const {
    fields: pairs,
    append: appendLeftItem,
    remove: removeLeftItem,
  } = useFieldArray({
    control,
    name: `quizList.${index}.matchData.leftItems`,
  });
  const { append: appendRightItem, remove: removeRightItem } = useFieldArray({
    control,
    name: `quizList.${index}.matchData.rightItems`,
  });
  const { append: appendAnswerPair, remove: removeAnswerPair } = useFieldArray({
    control,
    name: `quizList.${index}.matchData.answerPairs`,
  });

  // useFieldArray의 fields(pairs)가 leftItems 개수를 반응적으로 반영하므로 별도 useWatch가 불필요하다.
  const pairCount = pairs.length;

  const handleAdd = useCallback(() => {
    if (pairCount >= MAX_MATCH_PAIR_COUNT) return;

    const leftId = nanoid(8);
    const rightId = nanoid(8);

    appendLeftItem({ id: leftId, text: '', richText: '', imageUrl: undefined });
    appendRightItem({
      id: rightId,
      text: '',
      richText: '',
      imageUrl: undefined,
    });
    appendAnswerPair({ leftId, rightId });
    focusField(`quizList.${index}.matchData.leftItems.${pairCount}.text`, {
      scroll: true,
      delay: 100,
    });
  }, [appendAnswerPair, appendLeftItem, appendRightItem, index, pairCount]);

  const handleRemove = useCallback(
    (pairIndex: number) => {
      if (pairCount <= MIN_MATCH_PAIR_COUNT) return;

      removeLeftItem(pairIndex);
      removeRightItem(pairIndex);
      removeAnswerPair(pairIndex);
    },
    [pairCount, removeAnswerPair, removeLeftItem, removeRightItem],
  );

  return {
    pairs,
    handleAdd,
    handleRemove,
    canAdd: pairCount < MAX_MATCH_PAIR_COUNT,
    canRemove: pairCount > MIN_MATCH_PAIR_COUNT,
  };
};
